import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  ShieldCheck,
  ShieldX,
  Plus,
  Trash2,
  X,
  ExternalLink,
  Search,
} from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { z } from "zod";

import {
  ConfirmClaimsDialog,
  NotConfirmDialog,
  type DialogClaim,
} from "~/components/cco-claims/decision-dialogs";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "~/components/ui/command";
import { EmptyState } from "~/components/ui/empty-state";
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "~/components/ui/popover";
import { cn } from "~/lib/cn";
import { requirePermission } from "~/lib/functions/admin-access";
import {
  ccoDropdownQueryOptions,
  getAdminCtpsFn,
} from "~/lib/functions/admin-ctps";
import {
  adminDecideClaimsFn,
  createCcoMembershipFn,
  createCtpCertificationFn,
  deleteCcoMembershipFn,
  deleteCtpCertificationFn,
  getAdminClaimsFn,
  openClaimCountsQueryOptions,
  searchCoachesFn,
  verificationQueryKeys,
} from "~/lib/functions/admin-verifications";
import { ClaimVerificationState } from "~/lib/schemas/db-enums";
import {
  DCM_DECIDER_NAME,
  claimKey,
  deciderName,
  formatVerificationMonth,
  type AdminClaim,
  type ClaimKind,
} from "~/lib/shared/cco-claims";

const verificationsSearchSchema = z.object({
  tab: z.enum(["cco", "ctp"]).optional(),
  ccoId: z.string().uuid().optional(),
  ctpId: z.string().uuid().optional(),
  /** Opens on claims whose coach asked DCM to review them. */
  review: z.boolean().optional(),
});

export const Route = createFileRoute("/admin/verifications/")({
  staticData: { title: "Coach Verifications" },
  validateSearch: verificationsSearchSchema,
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "coaches:read"),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(openClaimCountsQueryOptions),
      context.queryClient.ensureQueryData(ccoDropdownQueryOptions),
    ]);
    return {};
  },
  component: VerificationsAdminPage,
});

type ActiveTab = "cco" | "ctp";
type StateFilter = "all" | ClaimVerificationState | "review";

interface TargetOption {
  id: string;
  name: string;
}

const ctpDropdownQueryOptions = {
  queryKey: ["admin-verifications", "ctp-dropdown"] as const,
  queryFn: async (): Promise<TargetOption[]> => {
    const rows = await getAdminCtpsFn();
    return rows.map((r) => ({ id: r.id, name: r.name }));
  },
};

const INPUT_CLASS =
  "rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100";

function formatDate(date: Date | string | null): string {
  if (!date) return "-";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "Invalid";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const STATE_LABEL: Record<ClaimVerificationState, string> = {
  [ClaimVerificationState.Waiting]: "Waiting",
  [ClaimVerificationState.Verified]: "Verified",
  [ClaimVerificationState.NotConfirmed]: "Not confirmed",
};

function StateBadge({ claim }: { claim: AdminClaim }) {
  return (
    <div className="space-y-1">
      <span
        className={cn(
          "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
          claim.state === ClaimVerificationState.Verified &&
            "bg-emerald-100 text-emerald-700",
          claim.state === ClaimVerificationState.Waiting &&
            "bg-amber-100 text-amber-700",
          claim.state === ClaimVerificationState.NotConfirmed &&
            "bg-red-100 text-red-700",
        )}
      >
        {STATE_LABEL[claim.state]}
      </span>
      {claim.reviewRequestedAt && (
        <span className="ml-1 inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700">
          Review requested
        </span>
      )}
    </div>
  );
}

/** Who decided, and the badge date or the decision day. */
function DecisionCell({ claim }: { claim: AdminClaim }) {
  if (!claim.decider) {
    return (
      <span className="text-gray-400">Added {formatDate(claim.createdAt)}</span>
    );
  }
  return (
    <div className="space-y-0.5 text-xs text-gray-600">
      <div className="font-medium text-gray-800">
        {deciderName(claim.decider)}
      </div>
      <div>
        {claim.verificationDate
          ? `Shows ${formatVerificationMonth(claim.verificationDate)}`
          : "No date shown"}
        {claim.decidedAt ? ` · decided ${formatDate(claim.decidedAt)}` : ""}
      </div>
      {claim.decisionNote && (
        <div className="text-gray-500">Note: {claim.decisionNote}</div>
      )}
      {claim.reviewMessage && (
        <div className="text-purple-700">Coach: {claim.reviewMessage}</div>
      )}
    </div>
  );
}

interface CoachSearchResult {
  id: string;
  fullName: string;
  email: string;
}

function CoachSearchInput({
  selectedCoach,
  onSelect,
  onClear,
}: {
  selectedCoach: CoachSearchResult | null;
  onSelect: (coach: CoachSearchResult) => void;
  onClear: () => void;
}) {
  const [input, setInput] = useState("");
  const [debouncedInput, setDebouncedInput] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedInput(input), 300);
    return () => clearTimeout(timer);
  }, [input]);

  const { data: results = [], isLoading } = useQuery({
    queryKey: ["admin-verifications", "coach-search", debouncedInput],
    queryFn: () => searchCoachesFn({ data: { search: debouncedInput } }),
    enabled: debouncedInput.length >= 2,
  });

  useEffect(() => {
    if (results.length > 0) setOpen(true);
  }, [results]);

  if (selectedCoach) {
    return (
      <div className="flex items-center gap-2">
        <div className={cn(INPUT_CLASS, "flex flex-1 items-center gap-2")}>
          <span className="font-medium text-gray-900">
            {selectedCoach.fullName}
          </span>
          <span className="text-gray-400">{selectedCoach.email}</span>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <Command shouldFilter={false} className="overflow-visible">
          <CommandInput
            value={input}
            onValueChange={(value) => {
              setInput(value);
              if (value.length < 2) setOpen(false);
            }}
            placeholder="Search coach by name or email..."
          />
        </Command>
      </PopoverAnchor>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandList>
            {isLoading ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">
                Searching...
              </div>
            ) : results.length === 0 ? (
              <CommandEmpty>No coaches found.</CommandEmpty>
            ) : (
              <CommandGroup>
                {results.map((coach) => (
                  <CommandItem
                    key={coach.id}
                    value={coach.id}
                    onSelect={() => {
                      onSelect(coach);
                      setInput("");
                      setOpen(false);
                    }}
                  >
                    <span className="font-medium text-gray-900">
                      {coach.fullName}
                    </span>
                    <span className="text-gray-400">{coach.email}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const KIND_COPY: Record<
  ClaimKind,
  { target: string; all: string; add: string; addTitle: string; select: string }
> = {
  membership: {
    target: "CCO",
    all: "All CCOs",
    add: "Add Membership",
    addTitle: "New CCO Membership",
    select: "Select a CCO...",
  },
  program: {
    target: "CTP",
    all: "All CTPs",
    add: "Add Certification",
    addTitle: "New CTP Certification",
    select: "Select a CTP...",
  },
};

function toDialogClaim(claim: AdminClaim): DialogClaim {
  return {
    key: claimKey(claim),
    coachName: claim.coachName,
    label: claim.targetName,
  };
}

function ClaimsTab({
  kind,
  options,
  initialTargetId = "",
  initialReview = false,
}: {
  kind: ClaimKind;
  options: TargetOption[];
  initialTargetId?: string;
  initialReview?: boolean;
}) {
  const copy = KIND_COPY[kind];
  const queryClient = useQueryClient();
  const [targetId, setTargetId] = useState(initialTargetId);
  const [stateFilter, setStateFilter] = useState<StateFilter>(
    initialReview ? "review" : "all",
  );
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState<AdminClaim[]>([]);
  const [rejecting, setRejecting] = useState<AdminClaim | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState<CoachSearchResult | null>(
    null,
  );
  const [createTargetId, setCreateTargetId] = useState("");

  const filters = useMemo(
    () => ({
      kind,
      targetId: targetId || undefined,
      state:
        stateFilter === "all" || stateFilter === "review"
          ? undefined
          : stateFilter,
      reviewRequested: stateFilter === "review" ? true : undefined,
      search: search || undefined,
    }),
    [kind, targetId, stateFilter, search],
  );

  const { data: rows = [], isLoading } = useQuery({
    queryKey: verificationQueryKeys.claims(filters),
    queryFn: () => getAdminClaimsFn({ data: filters }),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["admin-verifications"] });

  const decide = useMutation({
    mutationFn: (input: {
      claims: AdminClaim[];
      decision:
        | ClaimVerificationState.Verified
        | ClaimVerificationState.NotConfirmed;
      verificationDate: string | null;
      note: string | null;
    }) =>
      adminDecideClaimsFn({
        data: {
          claims: input.claims.map(({ kind: k, coachId, targetId: t }) => ({
            kind: k,
            coachId,
            targetId: t,
          })),
          decision: input.decision,
          verificationDate: input.verificationDate,
          note: input.note,
        },
      }),
    onSuccess: async () => {
      setSelected(new Set());
      setConfirming([]);
      setRejecting(null);
      await refresh();
    },
  });

  const createMutation = useMutation({
    mutationFn: (input: { coachId: string; targetId: string }) =>
      kind === "membership"
        ? createCcoMembershipFn({
            data: { coachId: input.coachId, ccoId: input.targetId },
          })
        : createCtpCertificationFn({
            data: { coachId: input.coachId, ctpId: input.targetId },
          }),
    onSuccess: async () => {
      setSelectedCoach(null);
      setCreateTargetId("");
      setShowCreateForm(false);
      await refresh();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (claim: AdminClaim) =>
      kind === "membership"
        ? deleteCcoMembershipFn({
            data: { coachId: claim.coachId, ccoId: claim.targetId },
          })
        : deleteCtpCertificationFn({
            data: { coachId: claim.coachId, ctpId: claim.targetId },
          }),
    onSuccess: refresh,
  });

  const allSelected =
    rows.length > 0 && rows.every((r) => selected.has(claimKey(r)));

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(rows.map(claimKey)));
  };

  const toggleRow = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedRows = rows.filter((r) => selected.has(claimKey(r)));

  return (
    <>
      <ConfirmClaimsDialog
        claims={confirming.map(toDialogClaim)}
        decider={DCM_DECIDER_NAME}
        onOpenChange={(open) => !open && setConfirming([])}
        onConfirm={(verificationDate) =>
          decide.mutateAsync({
            claims: confirming,
            decision: ClaimVerificationState.Verified,
            verificationDate,
            note: null,
          })
        }
      />
      <NotConfirmDialog
        claim={rejecting ? toDialogClaim(rejecting) : null}
        organization={rejecting?.organization ?? ""}
        onOpenChange={(open) => !open && setRejecting(null)}
        onConfirm={(note) =>
          rejecting
            ? decide.mutateAsync({
                claims: [rejecting],
                decision: ClaimVerificationState.NotConfirmed,
                verificationDate: null,
                note,
              })
            : Promise.resolve()
        }
      />

      {/* Create form toggle */}
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
        >
          <Plus className="h-4 w-4" />
          {copy.add}
        </button>
      </div>

      {showCreateForm && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-medium tracking-wide text-gray-500 uppercase">
              {copy.addTitle}
            </h3>
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(false);
                setSelectedCoach(null);
                setCreateTargetId("");
              }}
              className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Coach
              </label>
              <CoachSearchInput
                selectedCoach={selectedCoach}
                onSelect={setSelectedCoach}
                onClear={() => setSelectedCoach(null)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {copy.target}
              </label>
              <select
                value={createTargetId}
                onChange={(e) => setCreateTargetId(e.target.value)}
                className={cn(INPUT_CLASS, "w-full")}
              >
                <option value="">{copy.select}</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
            {createMutation.error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {createMutation.error instanceof Error
                  ? createMutation.error.message
                  : "Failed to create."}
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                if (!selectedCoach || !createTargetId) return;
                createMutation.mutate({
                  coachId: selectedCoach.id,
                  targetId: createTargetId,
                });
              }}
              disabled={
                !selectedCoach || !createTargetId || createMutation.isPending
              }
              className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {createMutation.isPending ? "Creating..." : copy.add}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          className={INPUT_CLASS}
        >
          <option value="">{copy.all}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>

        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value as StateFilter)}
          className={INPUT_CLASS}
        >
          <option value="all">All states</option>
          <option value={ClaimVerificationState.Waiting}>Waiting</option>
          <option value={ClaimVerificationState.Verified}>Verified</option>
          <option value={ClaimVerificationState.NotConfirmed}>
            Not confirmed
          </option>
          <option value="review">Review requested</option>
        </select>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search coach name or email..."
          className={cn(INPUT_CLASS, "w-64")}
        />
      </div>

      {/* Bulk actions */}
      {selectedRows.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <span className="text-sm text-gray-600">
            {selectedRows.length} selected
          </span>
          <button
            type="button"
            onClick={() => setConfirming(selectedRows)}
            disabled={decide.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
          >
            <ShieldCheck className="h-4 w-4" />
            Verify selected
          </button>
        </div>
      )}

      {decide.error && !confirming.length && !rejecting && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {decide.error.message}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="px-6 py-8 text-center text-sm text-gray-400">
            Loading...
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            size="sm"
            icon={Search}
            title="No matches"
            description="Try adjusting your filters."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left text-xs tracking-wider text-gray-500 uppercase">
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-4 py-3">Coach</th>
                  <th className="px-4 py-3">{copy.target}</th>
                  <th className="px-4 py-3">State</th>
                  <th className="px-4 py-3">Decision</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const key = claimKey(row);
                  const targetPath =
                    kind === "membership"
                      ? `/organizations/${row.targetSlug}`
                      : `/training-programs/${row.targetSlug}`;
                  return (
                    <tr
                      key={key}
                      className="border-b align-top last:border-0 hover:bg-gray-50"
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(key)}
                          onChange={() => toggleRow(key)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <a
                          href={`/${row.coachUsername}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-gray-900 hover:text-brand-600 hover:underline"
                        >
                          {row.coachName}
                          <ExternalLink className="ml-1 inline h-3 w-3 text-gray-400" />
                        </a>
                        <div className="text-xs text-gray-500">
                          {row.coachEmail}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.targetPublished ? (
                          <a
                            href={targetPath}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-brand-600 hover:underline"
                          >
                            {row.targetName}
                            <ExternalLink className="ml-1 inline h-3 w-3 text-gray-400" />
                          </a>
                        ) : (
                          row.targetName
                        )}
                        {kind === "program" && (
                          <div className="text-xs text-gray-500">
                            {row.organization}
                            {row.certificationDate
                              ? ` · certified ${formatDate(row.certificationDate)}`
                              : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StateBadge claim={row} />
                      </td>
                      <td className="px-4 py-3">
                        <DecisionCell claim={row} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {row.state !== ClaimVerificationState.Verified && (
                            <button
                              type="button"
                              onClick={() => setConfirming([row])}
                              disabled={decide.isPending}
                              className="inline-flex items-center gap-1 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
                            >
                              <ShieldCheck className="h-3.5 w-3.5" />
                              {row.reviewRequestedAt ? "Restore" : "Verify"}
                            </button>
                          )}
                          {(row.state !== ClaimVerificationState.NotConfirmed ||
                            row.reviewRequestedAt) && (
                            <button
                              type="button"
                              onClick={() => setRejecting(row)}
                              disabled={decide.isPending}
                              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
                            >
                              <ShieldX className="h-3.5 w-3.5" />
                              {row.reviewRequestedAt
                                ? "Keep decision"
                                : "Not confirmed"}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                !window.confirm(
                                  `Remove ${row.coachName}'s claim for ${row.targetName}? This cannot be undone.`,
                                )
                              )
                                return;
                              deleteMutation.mutate(row);
                            }}
                            disabled={deleteMutation.isPending}
                            className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                            title="Remove claim"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function VerificationsAdminPage() {
  const {
    tab: initialTab,
    ccoId: initialCcoId,
    ctpId: initialCtpId,
    review,
  } = Route.useSearch();
  const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab ?? "cco");

  const { data: counts } = useQuery(openClaimCountsQueryOptions);
  const { data: ccoOptions = [] } = useQuery(ccoDropdownQueryOptions);
  const { data: ctpOptions = [] } = useQuery(ctpDropdownQueryOptions);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl">
        <h1 className="mb-2 text-2xl font-medium text-gray-900">
          Coach Verifications
        </h1>
        <p className="mb-6 text-sm text-gray-500">
          Verify coaches&apos; organization memberships and program claims as
          Dream Coach Match, mark them not confirmed, and answer review
          requests. Representatives decide claims for their own organization.
        </p>

        <div className="mb-6 grid grid-cols-3 gap-4">
          {[
            [counts?.waitingMemberships, "Memberships waiting"],
            [counts?.waitingPrograms, "Program claims waiting"],
            [counts?.reviewRequests, "Review requests"],
          ].map(([value, label]) => (
            <div
              key={label}
              className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div className="text-2xl font-medium text-gray-900">
                {value ?? "-"}
              </div>
              <div className="text-xs text-gray-500">{label}</div>
            </div>
          ))}
        </div>

        <div className="mb-6 flex gap-1 rounded-lg bg-gray-100 p-1">
          {(
            [
              ["cco", "CCO Memberships"],
              ["ctp", "CTP Certifications"],
            ] as const
          ).map(([tab, label]) => (
            <button
              key={tab}
              type="button"
              className={cn(
                "flex-1 rounded-md px-4 py-2 text-sm font-medium transition",
                activeTab === tab
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700",
              )}
              onClick={() => setActiveTab(tab)}
            >
              {label}
            </button>
          ))}
        </div>

        {activeTab === "cco" ? (
          <ClaimsTab
            key="cco"
            kind="membership"
            options={ccoOptions}
            initialTargetId={initialCcoId ?? ""}
            initialReview={review}
          />
        ) : (
          <ClaimsTab
            key="ctp"
            kind="program"
            options={ctpOptions}
            initialTargetId={initialCtpId ?? ""}
            initialReview={review}
          />
        )}
      </div>
    </div>
  );
}
